import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/utils/scroll";

const HOME_ANCHORS = [
  { label: "Process", id: "process" },
  { label: "Why Us", id: "why" },
];

const PAGE_LINKS = [
  { label: "Catalog", to: "/catalog" },
  { label: "The Name", to: "/story" },
  { label: "FAQ", to: "/faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goAnchor = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 500);
    } else {
      scrollToId(id);
    }
  };

  const navCls = (active) =>
    `font-mono text-xs uppercase tracking-widest transition-colors ${active ? "text-black" : "text-zinc-500 hover:text-black"}`;

  return (
    <header data-testid="main-navbar" className="fixed top-0 inset-x-0 z-40 border-b border-zinc-200 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Link data-testid="nav-logo" to="/" onClick={() => setOpen(false)} className="flex items-center gap-2 text-left">
          <span className="h-2 w-2 rounded-full bg-tour-red animate-pulse-dot" />
          <span className="font-display text-sm font-extrabold tracking-tight uppercase">Poitín Republic</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {HOME_ANCHORS.map((l) => (
            <button key={l.id} data-testid={`nav-link-${l.id}`} onClick={() => goAnchor(l.id)} className={navCls(false)}>
              {l.label}
            </button>
          ))}
          {PAGE_LINKS.map((l) => (
            <Link key={l.to} data-testid={`nav-link-${l.to.slice(1)}`} to={l.to} className={navCls(location.pathname === l.to)}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-start-enquiry-btn"
            onClick={() => { setOpen(false); navigate("/enquiry"); }}
            className="group hidden items-center gap-1.5 bg-tour-void px-5 py-2.5 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-tour-red sm:flex"
          >
            Start Enquiry
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button data-testid="nav-mobile-menu-btn" onClick={() => setOpen(!open)} className="md:hidden" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-zinc-200 bg-white md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {HOME_ANCHORS.map((l) => (
                <button key={l.id} data-testid={`nav-mobile-link-${l.id}`} onClick={() => goAnchor(l.id)} className="py-2 text-left font-mono text-sm uppercase tracking-widest text-zinc-600">
                  {l.label}
                </button>
              ))}
              {PAGE_LINKS.map((l) => (
                <Link key={l.to} data-testid={`nav-mobile-link-${l.to.slice(1)}`} to={l.to} onClick={() => setOpen(false)} className="py-2 font-mono text-sm uppercase tracking-widest text-zinc-600">
                  {l.label}
                </Link>
              ))}
              <button data-testid="nav-mobile-start-enquiry-btn" onClick={() => { setOpen(false); navigate("/enquiry"); }} className="mt-2 bg-tour-void px-5 py-3 text-center font-mono text-xs uppercase tracking-widest text-white">
                Start Enquiry
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
